import { forwardRef, useState } from "react";
import { Box, Typography, Button, Tooltip, Collapse } from "@mui/material";
import { Info } from "lucide-react";
import useIsMobile from "components/hooks/useIsMobile";
import {
  MEMBERSHIP_OPTIONS,
  MembershipOption,
  StepProps,
  StepRef,
  STEPS,
  getDefaultPlanFromButton,
} from "./../types";
import { PlanFeatures } from "./../components/PlanFeatures";
import { StepHeader } from "../components/StepHeader";
import { FAQ } from "../components/FAQ";
import { MembershipOptionCard } from "../components/MembershipOptionCard";
import FlexBox from "components/common/FlexBox";

export default forwardRef<StepRef, StepProps>(
  ({ onNext, disabled, signupState }: StepProps, _ref) => {
    const isMobile = useIsMobile();
    const [showHiddenPlans, setShowHiddenPlans] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<number | null>(() => {
      if (signupState.membership) {
        const index = MEMBERSHIP_OPTIONS.findIndex(
          (m: MembershipOption) => m.name === signupState.membership?.name,
        );
        return index >= 0 ? index : null;
      }

      return getDefaultPlanFromButton(signupState.buttonParam);
    });

    const handleContinue = async () => {
      if (selectedPlan === null) return;
      await onNext?.({ membership: MEMBERSHIP_OPTIONS[selectedPlan] });
    };
    const hiddenPlans = MEMBERSHIP_OPTIONS.filter((m) => m.hidden);
    const visiblePlans = MEMBERSHIP_OPTIONS.filter((m) => !m.hidden);
    const hasAsterisk = MEMBERSHIP_OPTIONS.some((m) => m.promotionsDisabled);

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "0px 8px",
          gap: "16px",
        }}
      >
        <StepHeader
          title="Choose your membership"
          tooltip={
            <Tooltip
              placement={isMobile ? "bottom" : "bottom-end"}
              enterTouchDelay={0}
              leaveTouchDelay={5000}
              title={
                <Box sx={{ padding: "12px" }}>
                  <Typography variant="bodyHeavy" mb="4px">
                    Happiness guarantee
                  </Typography>
                  <Typography variant="body">
                    If your Advisor isn't providing the mental load relief you
                    need, let us know and we'll find a better match.
                  </Typography>
                </Box>
              }
            >
              <Info size={20} color="#8E9598" />
            </Tooltip>
          }
        />

        <Box
          sx={{
            display: "flex",
            gap: isMobile ? "24px" : "48px",
            justifyContent: "center",
            alignSelf: "center",
            flexDirection: isMobile ? "column" : "row",
            width: "100%",
            maxWidth: "800px",
          }}
        >
          {/* Left side */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flex: 1,
              gap: "12px",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                width: "100%",
                minWidth: isMobile ? "none" : "350px",
              }}
            >
              {visiblePlans.map((option, idx) => {
                return (
                  <Box
                    onClick={() => !disabled && setSelectedPlan(idx)}
                    key={option.name}
                    sx={{ cursor: "pointer" }}
                  >
                    <MembershipOptionCard
                      option={option}
                      isSelected={selectedPlan === idx}
                    />
                  </Box>
                );
              })}
              {hiddenPlans.length > 0 && (
                <Collapse in={showHiddenPlans && hiddenPlans.length > 0}>
                  {hiddenPlans.map((option, idx) => {
                    return (
                      <Box
                        onClick={() => !disabled && setSelectedPlan(idx)}
                        key={option.name}
                        sx={{ cursor: "pointer" }}
                      >
                        <MembershipOptionCard
                          option={option}
                          isSelected={selectedPlan === idx}
                        />
                      </Box>
                    );
                  })}
                </Collapse>
              )}
              <Box
                sx={{
                  display:
                    hiddenPlans.length > 0 || hasAsterisk ? "flex" : "none",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop:
                    hiddenPlans.length > 0 && !showHiddenPlans
                      ? "-12px"
                      : "0px",
                }}
              >
                <Button
                  variant="text"
                  size="small"
                  sx={{
                    height: "unset",
                    visibility:
                      hiddenPlans.length > 0 && !showHiddenPlans
                        ? "visible"
                        : "hidden",
                  }}
                  onClick={() => {
                    setShowHiddenPlans(true);
                  }}
                >
                  Show more
                </Button>
                {hasAsterisk && (
                  <Typography
                    variant="bodySmall"
                    color="textSecondary"
                    sx={{ justifySelf: "flex-end" }}
                  >
                    *Excluded from promotions
                  </Typography>
                )}
              </Box>
            </Box>
            {/* {!isMobile && (
              <Button
                variant="text"
                disabled={disabled}
                onClick={() =>
                  onNext?.({
                    flowPath: STEPS.CONSULTATION_DETAILS,
                  })
                }
              >
                Not sure? Schedule a consultation
              </Button>
            )} */}
          </Box>

          {/* Right side */}
          <Box
            sx={{
              display: "flex",
              flex: 1,
              flexDirection: "column",
              gap: isMobile ? "12px" : "32px",
              justifyContent: "space-between",
            }}
          >
            <FlexBox direction="column" sx={{ gap: isMobile ? 0 : "24px" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <Typography variant="h4">All plans include</Typography>
                <PlanFeatures />
              </Box>

              <FAQ
                question="How to choose the best plan for you"
                answer="Most families with kids or multiple account holders start with the 8-hour plan, which works well for a fuller to-do list. If your needs are lighter, the 4-hour plan is a great place to start. All plans are month to month, so you can adjust as life changes."
              />
            </FlexBox>

            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "end",
                alignItems: "center",
                position: "relative",
                bottom: isMobile ? "0px" : "",
                width: isMobile ? "100%" : "auto",
                backgroundColor: "#FDFAF7",
                paddingBottom: isMobile ? "8px" : "0px",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleContinue}
                disabled={selectedPlan === null || disabled}
                sx={{
                  width: isMobile ? "100%" : "auto",
                  bgcolor: "#198282",
                  "&:hover": {
                    bgcolor: "#276767",
                  },
                  "&.Mui-disabled": { backgroundColor: "#EAEBEC" },
                  borderRadius: "32px",
                  fontSize: "1.15rem",
                  px: 4,
                  textTransform: "none",
                  boxShadow: isMobile
                    ? "0px -1px 20px 5px rgba(255, 255, 255, .2)"
                    : "",
                }}
              >
                Continue
              </Button>
              {isMobile && (
                <Button
                  variant="text"
                  disabled={disabled}
                  onClick={() =>
                    onNext?.({
                      flowPath: STEPS.CONSULTATION_DETAILS,
                    })
                  }
                >
                  Or schedule a consultation
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  },
);
